<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div class="m-3" v-else-if="!hasPermission">
    <access-denied />
  </div>
  <div v-else>
    <div class="d-flex border-bottom mb-3 justify-content-between align-items-end">
      <div class="overflow-auto hide-scrollbar flex-grow-1">
        <ul class="nav nav-custom flex-nowrap">
          <li class="nav-item" v-for="viewpoint in viewpoints">
            <a class="nav-link text-nowrap" :class="params.viewpoint == viewpoint.value ? 'active' : ''"
              @click="setViewpoint(viewpoint.value)" style="cursor: pointer;">
              {{ $t(viewpoint.text) }}
              <span class="badge rounded-pill ms-1" :class="[viewpoint.bgc]">
                {{ viewpoint.count }}
              </span>
            </a>
          </li>
        </ul>
      </div>
      <column-toggle
        :headers="allHeaders"
        storage-key="products.columns"
        v-model="visibleColumns"
      />
    </div>

    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mx-3 mb-3 gap-3">
      <div class="d-flex flex-column flex-sm-row gap-2">
        <button type="button" class="btn btn-sm btn-primary text-nowrap" @click="() => {
          offcanvasFrom.open = true;
          offcanvasFrom.type = 'CREATE';
        }">
          <i class="fa-solid fa-circle-plus me-2" />
          {{ $t('Create') }}
        </button>

        <div class="input-group input-group-sm search-input-wrapper">
          <input type="search" class="form-control shadow-none" :placeholder="$t('search') + '...'" aria-label="Search"
            aria-describedby="search-addon" v-model.trim="params.search" @keyup.enter="fetchData">
          <button class="input-group-text bg-white" type="button" @click="fetchData">
            <i class="fas fa-search" />
          </button>
        </div>

        <button type="button" class="btn btn-sm btn-outline-secondary text-nowrap position-relative"
          @click="offcanvasFilter.open = true">
          <i class="fa-solid fa-filter me-1" />
          {{ $t('Filters') }}
          <span v-if="activeFilterCount" class="badge bg-primary ms-1 rounded-pill">
            {{ activeFilterCount }}
          </span>
        </button>
      </div>

      <div class="d-flex justify-content-start justify-content-lg-end">
        <per-page @per-page="setPerPage" />
      </div>
    </div>

    <vue3-easy-data-table v-model:server-options="datatable.serverOptions" ref="dataTable"
      :headers="visibleHeaders" :items="datatable.items" :loading="datatable.loading"
      :server-items-length="datatable.total" :theme-color="'#515151'" :hide-footer="false" :hide-rows-per-page="true"
      :empty-message="$t('No Available Data')" :table-min-height="null" buttons-pagination
      :body-item-class-name="bodyItemClassNameFunction" table-class-name='customize-table'>
      <template #header-price="header">
        <span class="w-100 text-end">
          {{ header.text }}
        </span>
      </template>
      <template #header-created_by="header">
        <span class="w-100 text-end">
          {{ header.text }}
        </span>
      </template>
      <template #header-action="header">
        <span class="w-100 text-end">
          {{ header.text }}
        </span>
      </template>
      <template #item-image="item">
        <img v-if="item.thumbnail_url && item.image_url" class="img-table" :src="item.thumbnail_url"
          loading="lazy" decoding="async"
          @click="showImage(item.image_url)" style="cursor: pointer;">
        <img v-else :src="$storeIndex.getImageSrc('')" width="25px" height="25px">
      </template>
      <template #item-name="item">
        {{ $ln(item) }}
        <span v-if="item.show_in_menu === false" class="badge bg-warning-subtle text-warning border border-warning-subtle ms-1" :title="$t('Hide to use as reward-only (promotion/loyalty).')">
          <i class="fa-solid fa-gift me-1" />{{ $t('Reward-only') }}
        </span><br>
        <span v-if="item.variants_count > 0" class="text-secondary">
          {{ item.variants_count + ' ' + $t('Variants defined') }}
        </span>
      </template>
      <template #item-type="item">
        <span :class="['badge', viewpoints.find((viewpoint) => viewpoint.text == item.type).bgc]">
          {{ $t(item.type) }}
        </span>
      </template>
      <template #item-category="item">
        {{ $ln(item, 'category') }}
      </template>
      <template #item-price="item">
        {{ formatNumberTwoDigit(item.price) }}
      </template>
      <template #item-out_of_stock_branches="item">
        <div
          v-if="item.out_of_stock_branches && item.out_of_stock_branches.length"
          class="d-flex flex-wrap gap-1"
        >
          <span
            v-for="branch in item.out_of_stock_branches"
            :key="branch.branch_id"
            class="badge bg-danger-subtle text-danger border border-danger-subtle"
          >
            <i class="fa-solid fa-store me-1" />
            {{ branch.name }}
          </span>
        </div>
        <span v-else class="text-muted">—</span>
      </template>
      <template #item-inactive_branches="item">
        <div
          v-if="item.inactive_branches && item.inactive_branches.length"
          class="d-flex flex-wrap gap-1"
        >
          <span
            v-for="branch in item.inactive_branches"
            :key="branch.branch_id"
            class="badge bg-secondary-subtle text-secondary border border-secondary-subtle"
          >
            <i class="fa-solid fa-store me-1" />
            {{ branch.name }}
          </span>
        </div>
        <span v-else class="text-muted">—</span>
      </template>
      <template #item-created_at="item">
        {{ formatDateTime(item.created_at) }}
      </template>

      <template #item-action="item">
        <div class="dropdown custom-actions">
          <button class="border-0 btn btn-sm action-btn" type="button" data-bs-toggle="dropdown"
            data-bs-display="static" aria-expanded="false">
            <i class="fa-solid fa-ellipsis-vertical px-1" />
          </button>

          <ul class="dropdown-menu dropdown-menu-end shadow-sm border-0 rounded-3 fs-7">
            <li>
              <a class="dropdown-item py-2" href="#" @click.prevent="viewInfo.open = true; viewInfo.id = item.id;">
                <i class="fa-solid fa-eye text-primary me-2 fw-bold" style="width: 16px;" /> {{ $t('View') }}
              </a>
            </li>
            <li>
              <a class="dropdown-item py-2" href="#"
                @click.prevent="offcanvasFrom.open = true; offcanvasFrom.id = item.id; offcanvasFrom.type = 'EDIT';">
                <i class="fa-solid fa-pen-to-square text-warning me-2 fw-bold" style="width: 16px;" /> {{ $t('Edit') }}
              </a>
            </li>

            <li>
              <hr class="dropdown-divider">
            </li>

            <!-- สูตร = ใช้ตัดสต็อก/คิดต้นทุนวัตถุดิบ — แพ็กไม่มี feature_inventory ไม่มีประโยชน์ ซ่อน (docs/plans-app.md) -->
            <li v-if="$hasFeature('feature_inventory')">
              <a class="dropdown-item py-2" href="#"
                @click.prevent="offcanvasRecipeFrom.open = true; offcanvasRecipeFrom.id = item.id;">
                <i class="fa-solid fa-book-open text-success me-2 fw-bold" style="width: 16px;" /> {{ $t('Recipe') }}
              </a>
            </li>
            <li>
              <a class="dropdown-item py-2" href="#"
                @click.prevent="offcanvasModifierForm.open = true; offcanvasModifierForm.id = item.id;">
                <i class="fa-solid fa-sliders text-secondary me-2 fw-bold" style="width: 16px;" /> {{ $t('Modifier') }}
              </a>
            </li>
            <li>
              <a class="dropdown-item py-2" href="#" @click.prevent="manageBranches(item.id)">
                <i class="fa-solid fa-store text-info me-2 fw-bold" style="width: 16px;" /> {{ $t('Manage Branches') }}
              </a>
            </li>

            <template v-if="!item.variant">
              <li>
                <hr class="dropdown-divider">
              </li>
              <li>
                <a class="dropdown-item py-2 text-danger" href="#" @click.prevent="handleDelete(item.id)">
                  <i class="fa-solid fa-trash-can me-2 fw-bold" style="width: 16px;" /> {{ $t('Delete') }}
                </a>
              </li>
            </template>
          </ul>
        </div>
      </template>

      <template #loading>
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </template>
    </vue3-easy-data-table>

    <offcanvas-form v-if="offcanvasFrom.open" :type="offcanvasFrom.type" :id="offcanvasFrom.id" />
    <offcanvas-info v-if="viewInfo.open" :id="viewInfo.id" @close-info="closeInfo" />
    <offcanvas-recipe-form v-if="offcanvasRecipeFrom.open" :id="offcanvasRecipeFrom.id"
      @close="() => { offcanvasRecipeFrom.open = false }" />
    <offcanvas-modifier-form v-if="offcanvasModifierForm.open" :id="offcanvasModifierForm.id"
      @close="() => { offcanvasModifierForm.open = false }" />
    <offcanvas-manage-branches v-if="manageBranchesForm.open" :id="manageBranchesForm.id"
      @close="manageBranchesForm.open = false" />
    <offcanvas-filter v-if="offcanvasFilter.open" :sections="filterSections"
      :value="{ category_ids: params.category_ids, branch_ids: params.branch_ids, stock_status: params.stock_status, active_status: params.active_status }" @apply="onApplyFilter"
      @close="offcanvasFilter.open = false" />
    <div class="modal fade" id="modalImage" ref="modalImage" aria-labelledby="modalImageLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <img :src="modalImage.image" class="d-block w-100" data-bs-dismiss="modal">
      </div>
    </div>
  </div>
</template>

<script lang="js">
import AccessDenied from "@/plugins/AccessDenied.vue";
import Vue3EasyDataTable from 'vue3-easy-data-table';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import PerPage from '@/plugins/PerPage.vue';
import FieldsDef from "./assets/template/datatable/FieldsDef";
import OffcanvasForm from './OffcanvasForm.vue';
import OffcanvasInfo from "./OffcanvasInfo.vue";
import OffcanvasRecipeForm from "./OffcanvasRecipeForm.vue";
import OffcanvasModifierForm from "./OffcanvasModifierForm.vue";
import OffcanvasManageBranches from "./OffcanvasManageBranches.vue";
import OffcanvasFilter from "@/components/shared/OffcanvasFilter.vue";
import ColumnToggle from '@/components/shared/ColumnToggle.vue';
import { loadVisibleColumns } from '@/utils/columnToggle';
import { Products } from '@/services/endpoint/products.service';
import moment from "moment";
import numeral from 'numeral';

export default {
  data() {
    return {
      hasPermission: false,
      params: {
        limit: 10,
        page: 1,
        search: '',
        viewpoint: 'all',
        category_ids: [],
        branch_ids: [],
        stock_status: [],
        active_status: []
      },
      filters: {
        category: [],
        branches: []
      },
      offcanvasFilter: {
        open: false
      },
      viewInfo: {
        open: false,
        id: 0,
      },
      offcanvasRecipeFrom: {
        open: false,
        id: 0,
      },
      offcanvasModifierForm: {
        open: false,
        id: 0,
      },
      manageBranchesForm: {
        open: false,
        id: 0,
      },
      pageLoading: false,
      datatable: {
        loading: false,
        headers: FieldsDef,
        items: [],
        total: 0,
        serverOptions: {
          page: 1,
          rowsPerPage: 10
        }
      },
      offcanvasFrom: {
        open: false,
        type: 'CREATE',
        id: 0,
      },
      viewpoints: [
        {
          value: 'all',
          text: 'All',
          count: 0,
          bgc: 'bg-primary text-white'
        },
        {
          value: 'simple',
          text: 'Simple',
          count: 0,
          bgc: 'bg-secondary text-white'
        },
        {
          value: 'config',
          text: 'Configurale',
          count: 0,
          bgc: 'bg-info text-dark'
        }
      ],
      modalImage: {
        open: false,
        image: ''
      },
      visibleColumns: loadVisibleColumns('products.columns', FieldsDef(t => t))
    }
  },
  components: {
    AccessDenied,
    PlaceholderContent,
    PerPage,
    Vue3EasyDataTable,
    OffcanvasForm,
    OffcanvasInfo,
    OffcanvasRecipeForm,
    OffcanvasModifierForm,
    OffcanvasManageBranches,
    OffcanvasFilter,
    ColumnToggle
  },
  computed: {
    activeFilterCount() {
      return this.params.category_ids.length + this.params.branch_ids.length + this.params.stock_status.length + this.params.active_status.length;
    },
    allHeaders() {
      return this.datatable.headers(this.$t);
    },
    visibleHeaders() {
      return this.allHeaders.filter(h => !h.togglable || this.visibleColumns.includes(h.value));
    },
    filterSections() {
      return [
        {
          key: 'branch_ids',
          label: 'Branch',
          icon: 'fa-store',
          options: this.filters.branches
        },
        {
          key: 'stock_status',
          label: 'Stock Status',
          icon: 'fa-box',
          single: true,
          options: [
            { id: 'out_of_stock', name: 'Out of Stock' },
            { id: 'in_stock', name: 'In Stock' }
          ]
        },
        {
          key: 'active_status',
          label: 'Active Status',
          icon: 'fa-toggle-on',
          single: true,
          options: [
            { id: 'active', name: 'Active' },
            { id: 'inactive', name: 'Inactive' }
          ]
        },
        {
          key: 'category_ids',
          label: 'Category',
          icon: 'fa-tag',
          options: this.filters.category
        }
      ];
    }
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;

    // ดักการ Scroll ในตาราง (overflow ภายใน): ถ้าตารางถูก Scroll ให้ปิด dropdown
    // เพราะตำแหน่ง fixed ของเมนูจะลอยอยู่ผิดที่
    this.$nextTick(() => {
      if (this.$refs.dataTable && this.$refs.dataTable.$el) {
        this.tableMainEl = this.$refs.dataTable.$el.querySelector('.vue3-easy-data-table__main');
        if (this.tableMainEl) {
          this.scrollListener = () => document.body.click();
          this.tableMainEl.addEventListener('scroll', this.scrollListener);
        }
      }
    });
  },
  unmounted() {
    if (this.scrollListener && this.tableMainEl) {
      this.tableMainEl.removeEventListener('scroll', this.scrollListener);
    }
  },
  methods: {
    manageBranches(id) {
      this.manageBranchesForm.id = id;
      this.manageBranchesForm.open = true;
    },
    closeInfo() {
      this.viewInfo.open = false;
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatDigit(value) {
      if ((value < 0.01) && value != 0) {
        return numeral(value).format("0,0.0000");
      }
      return numeral(value).format("0,0.00");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    bodyItemClassNameFunction(column, rowNumber) {
      let className = 'align-top py-1 px-2';
      const columnCenter = ['image'];
      const columnEnd = ['price', 'created_by'];
      if (columnCenter.includes(column)) {
        className += ' text-center';
      }
      if (columnEnd.includes(column)) {
        className += ' text-end';
      }
      if (column === 'action') {
        className += ' text-end overflow-visible';
      }
      return className;
    },
    async setViewpoint(tab) {
      if (this.params.viewpoint == tab) return;
      this.params.viewpoint = tab;
      await this.fetchData();
    },
    async fetchData() {
      this.datatable.loading = true;
      const { stock_status, active_status, ...requestParams } = this.params;
      // BE expects `is_in_stock` / `is_active` as 1/0; only send when a status is selected
      if (stock_status.length) {
        requestParams.is_in_stock = stock_status[0] === 'in_stock' ? 1 : 0;
      }
      if (active_status.length) {
        requestParams.is_active = active_status[0] === 'active' ? 1 : 0;
      }
      var queryString = '?' + $.param(requestParams);
      await Products.list(queryString).then(res => {
        this.datatable.items = res.data.data;
        this.datatable.total = res.data.meta.total;
        this.viewpoints.forEach(viewpoint => {
          viewpoint.count = res.data.viewpoint_cnt[viewpoint.value];
        });
        this.filters.category = res.data.filter?.category || [];
        this.filters.branches = res.data.filter?.branches || [];
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.pageLoading = false;
        }
      });

      this.datatable.loading = false;
      return;
    },
    onApplyFilter(payload) {
      this.params.category_ids = payload.category_ids || [];
      this.params.branch_ids = payload.branch_ids || [];
      this.params.stock_status = payload.stock_status || [];
      this.params.active_status = payload.active_status || [];
      this.params.page = 1;
      this.datatable.serverOptions.page = 1;
      this.fetchData();
    },
    setPerPage(perPage) {
      this.params.limit = perPage;
      this.$refs.dataTable.updateRowsPerPageActiveOption(perPage);
      this.fetchData();
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    async handleDelete(id) {
      try {
        let self = this;
        let title = this.$t("Delete Product") + '?';
        let html = this.$t("You will not be able to undo this action.");
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return Products.delete(id)
              .then((response) => {
                self.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Product Success!'));
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"/>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
    async showImage(image) {
      this.modalImage.image = image;
      $(this.$refs.modalImage).modal('show');
    }
  },
  watch: {
    "datatable.serverOptions"(nval, oval) {
      delete this.params.sort;
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
      if (nval.sortBy && nval.sortType) this.params.sort = nval.sortBy + "|" + nval.sortType;
      this.fetchData();
    }
  }
}
</script>
